import { useSelect } from "downshift";
import { useContext, useCallback, useRef, useState, type Ref, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "react-bootstrap";
import type { ValueLabel } from "../types/ValueLabel";
import ValidationContext from "./ValidationContext";

const useMergedRef = <T,>(
  ref1: Ref<T> | undefined,
  ref2: Ref<T> | undefined
): Ref<T> => {
  return useCallback(
    (element: T) => {
      if (ref1) {
        if (typeof ref1 === "function") {
          ref1(element);
        } else {
          ref1.current = element;
        }
      }
      if (ref2) {
        if (typeof ref2 === "function") {
          ref2(element);
        } else {
          ref2.current = element;
        }
      }
    },
    [ref1, ref2]
  );
};

interface Props<T extends string | number = string> {
  options: ValueLabel<T, string>[];
  value: T | ValueLabel<T, string> | null | undefined;
  label?: ReactNode;
  placeholder?: string;
  noResultsText?: string;
  disabled?: boolean;
  required?: boolean;
  helpTextAccordion?: ReactNode;
  clearable?: boolean;
  fieldName?: string;
  showValidation?: boolean;
  setValue: (v: ValueLabel<T, string> | null | undefined) => void;
}

export default function LargeSelectInputSelect<
  T extends string | number = string,
>({
  options,
  value,
  label,
  placeholder,
  noResultsText,
  disabled = false,
  required = false,
  helpTextAccordion,
  clearable = false,
  fieldName,
  showValidation = false,
  setValue,
}: Readonly<Props<T>>) {
  const { t } = useTranslation();
  const { suppressFieldErrors } = useContext(ValidationContext);
  const [liveMessage, setLiveMessage] = useState<string>("");

  const menuRef = useRef<HTMLUListElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const resolveValue = (
    v: T | ValueLabel<T, string> | null | undefined,
  ): T | null | undefined => {
    if (v === null || v === undefined) return v;
    if (typeof v === "object" && "value" in v) {
      return (v as ValueLabel<T, string>).value;
    }
    return v as T;
  };

  const resolvedValue = resolveValue(value);
  const selectedItem =
    resolvedValue !== null
      ? options.find((o) => o.value === resolvedValue) || null
      : null;
  const helpTextId = fieldName ? `${fieldName}-help` : undefined;
  const labelId = fieldName ? `${fieldName}-label` : undefined;
  const errorId = fieldName ? `${fieldName}-error` : undefined;
  const isInvalid = showValidation && required && !resolvedValue;
  const ariaDescribedBy = [
    helpTextAccordion && helpTextId ? helpTextId : undefined,
    isInvalid && errorId && !suppressFieldErrors ? errorId : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getToggleButtonProps,
    highlightedIndex,
    getItemProps,
    selectedItem: downshiftSelectedItem,
  } = useSelect<ValueLabel<T, string>>({
    items: options,
    selectedItem: selectedItem,
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      setValue(newSelectedItem ?? null);
      if (newSelectedItem) {
        setLiveMessage(
          t("largeSelectInput.a11y.selected", {
            item: newSelectedItem.label?.toString?.() ?? "",
          }),
        );
      }
    },
    itemToString: (item) => (item ? (item.label?.toString?.() ?? "") : ""),
  });

  const labelProps = getLabelProps({ id: labelId });
  
  const menuPropsBase = getMenuProps();
  const mergedMenuRef = useMergedRef((menuPropsBase as any).ref, menuRef);
  
  const toggleButtonPropsBase = getToggleButtonProps({
    id: fieldName,
    disabled,
    "aria-labelledby": labelId,
  });
  const mergedToggleRef = useMergedRef((toggleButtonPropsBase as any).ref, toggleRef);
  
  const menuProps = { ...menuPropsBase, ref: mergedMenuRef };
  const toggleProps = { ...toggleButtonPropsBase, ref: mergedToggleRef };

  return (
    <Form.Group className="form-group">
      <div className="large-select-input-wrapper">
        <label {...labelProps}>{label}</label>
        {helpTextAccordion}
        <div className="large-select-input-container">
          <button
            type="button"
            {...toggleProps}
            className="large-select-input"
            aria-describedby={ariaDescribedBy || undefined}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            {downshiftSelectedItem?.label?.toString?.() ??
              placeholder ??
              t("defaultSelectPlaceholder")}
          </button>
          {resolvedValue !== null && !disabled && clearable && (
            <button
              type="button"
              className="large-select-clear"
              onClick={() => {
                const removedMessage = t("largeSelectInput.a11y.removed", {
                  item: selectedItem?.label?.toString?.() ?? "",
                });
                setLiveMessage(removedMessage);
                setValue(null);
              }}
              aria-label={t("largeSelectInput.clearSelection")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="4" y1="4" x2="12" y2="12"></line>
                <line x1="12" y1="4" x2="4" y2="12"></line>
              </svg>
            </button>
          )}
          <span className="large-select-chevron">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="4 6 8 10 12 6"></polyline>
            </svg>
          </span>
        </div>
        <ul
          {...menuProps}
          className="large-select-menu"
          style={isOpen ? {} : { display: "none" }}
        >
          {isOpen &&
            (options.length > 0 ? (
              options.map((item, index) => (
                <li
                  key={`${item.value}`}
                  {...getItemProps({
                    index,
                    item,
                  })}
                  className={`large-select-item ${
                    downshiftSelectedItem?.value === item.value
                      ? "is-selected"
                      : ""
                  } ${highlightedIndex === index ? "is-highlighted" : ""}`}
                >
                  {item.label?.toString() ?? String(item.label)}
                </li>
              ))
            ) : (
              <li className="large-select-item-no-results">
                {noResultsText ?? t("largeSelectInput.a11y.noResults")}
              </li>
            ))}
        </ul>
        <div className="sr-only" aria-live="polite">
          {liveMessage}
        </div>
      </div>
      {/* Validation workaround for LargeSelectInput */}
      {fieldName && (
        <>
          <Form.Control
            id={fieldName}
            value={
              typeof value === "object" && value !== null && "value" in value
                ? value.value || ""
                : resolvedValue || ""
            }
            style={{ display: "none" }}
            required={required}
            aria-hidden="true"
            onChange={() => {}}
            tabIndex={-1}
          />
          {isInvalid && (
            <Form.Control.Feedback
              id={errorId}
              type="invalid"
              aria-hidden={suppressFieldErrors || undefined}
              style={{ display: "block" }}
            >
              {t(`${fieldName}.invalid`)}
            </Form.Control.Feedback>
          )}
        </>
      )}
    </Form.Group>
  );
}
